import { type FC, type FormEvent, type ReactNode, useEffect, useState } from "react";
import { match, P } from "ts-pattern";
import { Input } from "../../form/inputs/Input";
import { cn } from "../../lib/utils";
import { Banner, Button, ClickableIcon, Spinner, Text } from "../../primitives";
import { trackingAttrs } from "../../utils";
import { EmptyState } from "../empty-state";
import { RetryableLoadError } from "../retryable-load-error";
import { useViewerIds } from "./viewerIds";
import type { ViewState } from "./viewState";

const LOADING_DELAY_MS = 300;

/**
 * Covers the viewport area only, so the toolbar and the layout around it never jump. `name`
 * suffixes the viewer's test id.
 */
const Cover: FC<{ children: ReactNode; name: string; className?: string }> = ({
	children,
	name,
	className,
}) => {
	const ids = useViewerIds();
	return (
		<div
			className={cn(
				"absolute inset-0 z-10 flex items-center justify-center bg-surface-neutral p-6",
				className,
			)}
			data-testid={ids.testId(name)}
		>
			{children}
		</div>
	);
};

export const ViewerLoading: FC = () => {
	const [visible, setVisible] = useState(false);
	useEffect(() => {
		const timer = setTimeout(() => setVisible(true), LOADING_DELAY_MS);
		return () => clearTimeout(timer);
	}, []);
	return (
		<Cover name="loading">
			{visible && (
				<div className="flex flex-col items-center gap-2.5">
					<Spinner size="md" />
					<Text type="body" color="secondary">
						Loading document…
					</Text>
				</div>
			)}
		</Cover>
	);
};

export const ViewerLoadFailed: FC<{ onRetry: () => void; retrying: boolean }> = ({
	onRetry,
	retrying,
}) => {
	const ids = useViewerIds();
	return (
		<Cover name="failed">
			<RetryableLoadError
				resource="document"
				onRetry={onRetry}
				retrying={retrying}
				className="max-w-[420px]"
				testId={ids.testId("failed-retry")}
				trackingId={ids.trackingId("load-failed")}
				retryDataAttributes={ids.fileTracking}
			/>
		</Cover>
	);
};

export const ViewerMessage: FC<{
	/** Suffixes the viewer's test id, e.g. `no-document`. */
	name: string;
	icon?: "document-text" | "exclamation-triangle";
	message: string;
	action?: ReactNode;
}> = ({ name, icon, message, action }) => {
	const ids = useViewerIds();
	return (
		<Cover name={name}>
			<EmptyState
				icon={icon}
				message={message}
				action={action}
				testId={ids.testId(`${name}-message`)}
			/>
		</Cover>
	);
};

type ViewerPasswordPromptProps = {
	incorrect: boolean;
	checking: boolean;
	onSubmit: (password: string) => void;
};

/** Keeps what was typed after a wrong password, with the error under the field. */
export const ViewerPasswordPrompt: FC<ViewerPasswordPromptProps> = ({
	incorrect,
	checking,
	onSubmit,
}) => {
	const ids = useViewerIds();
	const [password, setPassword] = useState("");
	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (password !== "") onSubmit(password);
	};
	return (
		<Cover name="password">
			<EmptyState
				icon="lock-closed"
				message="This document is password protected."
				action={
					<form
						className="flex w-full max-w-[300px] flex-col items-center gap-3"
						onSubmit={handleSubmit}
					>
						<div className="flex w-full flex-col gap-1.5 text-left">
							<Input
								type="password"
								aria-label="Document password"
								aria-invalid={incorrect || undefined}
								placeholder="Password"
								error={incorrect}
								value={password}
								onChange={(event) => setPassword(event.target.value)}
								testId={ids.testId("password-input")}
								{...trackingAttrs(ids.trackingId("password"))}
								{...ids.fileTracking}
							/>
							{incorrect && (
								<Text type="caption" color="danger" data-testid={ids.testId("password-error")}>
									That password is incorrect.
								</Text>
							)}
						</div>
						<Button
							type="submit"
							color="primary"
							size="sm"
							loading={checking}
							disabled={password === ""}
							data-testid={ids.testId("password-submit")}
							trackingId={ids.trackingId("unlock")}
							{...ids.fileTracking}
						>
							Unlock
						</Button>
					</form>
				}
				testId={ids.testId("password-message")}
			/>
		</Cover>
	);
};

type ViewerOverlayProps = {
	view: ViewState;
	/** Fetches a fresh signed URL after the URL itself failed to load. */
	onRetryUrl: () => void;
	retryingUrl: boolean;
	/** Loads the document again after pdf.js failed on it. */
	onRetryLoad: () => void;
	onSubmitPassword: (password: string) => void;
	onDownload: () => void;
	downloading: boolean;
};

/** The state that stands in for the pages, or nothing once they are on screen. */
export const ViewerOverlay: FC<ViewerOverlayProps> = ({
	view,
	onRetryUrl,
	retryingUrl,
	onRetryLoad,
	onSubmitPassword,
	onDownload,
	downloading,
}) => {
	const ids = useViewerIds();
	return match(view)
		.with({ kind: "no-document" }, () => (
			<ViewerMessage name="no-document" icon="document-text" message="No document to show." />
		))
		.with({ kind: "loading" }, () => <ViewerLoading />)
		.with({ kind: "not-found" }, ({ message }) => (
			<ViewerMessage name="not-found" message={message} />
		))
		.with({ kind: P.union("url-failed", "load-failed") }, ({ kind }) => (
			<ViewerLoadFailed
				onRetry={kind === "url-failed" ? onRetryUrl : onRetryLoad}
				retrying={kind === "url-failed" && retryingUrl}
			/>
		))
		.with({ kind: "password" }, ({ incorrect, checking }) => (
			<ViewerPasswordPrompt incorrect={incorrect} checking={checking} onSubmit={onSubmitPassword} />
		))
		.with({ kind: "unreadable" }, () => (
			<ViewerMessage
				name="unreadable"
				icon="exclamation-triangle"
				message="This file can't be previewed."
				action={
					<Button
						color="primary"
						variant="outline"
						size="sm"
						loading={downloading}
						onClick={onDownload}
						data-testid={ids.testId("unreadable-download")}
						trackingId={ids.trackingId("unreadable-download")}
						{...ids.fileTracking}
					>
						Download
					</Button>
				}
			/>
		))
		.with({ kind: "ready" }, () => null)
		.exhaustive();
};

const NOTICE_CLASS_NAME = "rounded-none border-x-0 border-t-0 py-2";

/** A failed download or print, under the toolbar until dismissed. */
export const ViewerActionError: FC<{ message: string; onDismiss: () => void }> = ({
	message,
	onDismiss,
}) => {
	const ids = useViewerIds();
	return (
		<Banner tone="danger" className={NOTICE_CLASS_NAME} data-testid={ids.testId("action-error")}>
			<Banner.Content>
				<Text type="caption" color="danger" role="alert">
					{message}
				</Text>
			</Banner.Content>
			<Banner.Trailing>
				<ClickableIcon
					type="x-mark"
					size="small"
					color="secondary"
					aria-label="Dismiss"
					onClick={onDismiss}
					{...ids.control("action-error-dismiss")}
				/>
			</Banner.Trailing>
		</Banner>
	);
};

/** Says the selected field has no highlight, in place of scrolling to one. */
export const ViewerNoLocation: FC = () => {
	const ids = useViewerIds();
	return (
		<Banner
			tone="notification"
			className={NOTICE_CLASS_NAME}
			data-testid={ids.testId("no-location")}
		>
			<Text type="caption" color="secondary">
				This field isn't marked in the document.
			</Text>
		</Banner>
	);
};
